// The pure parts of the payment-routes page (spec: payment-gateway-routing, w-admin companion).
//
// Everything here is a function of the server's answer and nothing else. The server decides who
// takes a payment; this file only decides what to call it on screen — which is why it is the part
// with tests and the components are the part with a browser pass.

// How a gateway takes money on a rail, in words an operator can act on. The server sends the
// vocabulary (`prompt`, `checkout`, `manual`) and these are the sentences for it.
const INTERACTIONS = {
  prompt: 'Prompt on the phone',
  checkout: 'Hosted checkout page',
  manual: 'Our team confirms'
}

export const interactionLabel = interaction => INTERACTIONS[interaction] || '—'

// What a blank gateway is called. Blank is not missing: it means "whatever this rail defaults to",
// which is the state every row ships in, so it has to read as a choice rather than as a gap.
export const gatewayLabel = (key, gateways) => {
  if (!key) return 'Default'
  const found = (gateways || []).find(gateway => gateway.key === key)
  return (found && found.label) || key
}

// The warning when the owner's choice is recorded and is NOT in effect.
//
// This is the one thing the page exists to say that nothing else can. The server deliberately
// DEGRADES a route naming a gateway it has no credentials for — a staging box without Bachs keys
// still has to take payments — so the rail keeps working and the change silently does not apply.
// '' when there is nothing to warn about.
export const fallbackWarning = (row, gateways) => {
  if (!row || !row.fellBack) return ''
  const asked = gatewayLabel(row.requestedGateway || row.gateway, gateways)
  const used = gatewayLabel(row.effectiveGateway, gateways)
  if (!row.requestedGateway && !row.gateway) return ''
  return `${asked} is not set up on the server, so ${used} is being used.`
}

// Whether switching this rail off would leave the service with no way to be paid.
//
// Asked before the switch is sent, because the answer is a sentence the operator has to agree to:
// a service with no enabled rail shows a customer a payment screen with nothing on it.
export const isLastEnabled = (rails, rail) => {
  const enabled = (rails || []).filter(row => row.enabled)
  return enabled.length === 1 && enabled[0].rail === rail
}

// The order the rails are shown in: the server's `position`, then the rail name to break ties.
//
// Sorted here rather than trusted from the response because `position` is editable and two rows
// can hold the same number mid-edit — and a table that reorders itself differently on every
// render is a table nobody can click accurately.
export const sortRails = rails =>
  [...(rails || [])].sort((a, b) => {
    if (a.position !== b.position) return (a.position || 0) - (b.position || 0)
    return String(a.rail).localeCompare(String(b.rail))
  })

// Only the fields the server accepts, and only the ones that actually changed.
//
// A PATCH carrying a field at its current value would write a `PaymentRouteChange` row for a
// non-event, and a change log full of non-events is a log nobody reads.
export const buildRoutePayload = (row, change) => {
  const payload = {}
  if ('enabled' in change && change.enabled !== row.enabled) payload.enabled = change.enabled
  if ('position' in change && change.position !== row.position) payload.position = change.position
  if ('gateway' in change && change.gateway !== row.gateway) payload.gateway = change.gateway
  return payload
}

// A route change in one line, for the recent-changes list.
export const changeSentence = change => {
  const from = change.from || 'default'
  const to = change.to || 'default'
  return `${change.service} ${change.rail} · ${change.field}: ${from} → ${to}`
}

// What a refund button should say, and whether it may be pressed.
//
// `automatic: false` means no company holds the money — merchant and bank are the customer moving
// it themselves — so the honest button records a refund for a person to send rather than claiming
// to send one (companion Req 3.2).
export const refundButtonLabel = (summary, gateways) => {
  if (!summary) return 'Refund'
  if (summary.automatic) return 'Refund'
  return `Refund by hand on ${gatewayLabel(summary.gateway, gateways)}`
}
